import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn, EmptyState, brandName } from "@/lib/ui";
import { FolderLock, Download, Archive, Trash2, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Documents() {
  const api = useApi();
  const qc = useQueryClient();
  const { brand } = useBrand();
  const fileRef = useRef(null);
  const [category, setCategory] = useState("Other");
  const { data: cats } = useQuery({ queryKey: ["doc-cats"], queryFn: () => api.get("/documents/categories") });
  const { data: docs = [], isLoading } = useQuery({ queryKey: ["documents", brand], queryFn: () => api.get(`/documents?brand_id=${brand}`) });

  const upload = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); fd.append("brand_id", brand); fd.append("category", category); return api.upload("/documents", fd); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["documents"] }); toast.success("Document added to vault"); },
    onError: (e) => toast.error(e.message),
  });
  const archive = useMutation({
    mutationFn: (d) => api.patch(`/documents/${d.id}`, { archived: !d.archived }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });
  const del = useMutation({
    mutationFn: (id) => api.del(`/documents/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["documents"] }); toast.success("Document moved to trash"); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker={`${brandName(brand)} · Vault`} title="Document Vault">
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category"
          className="h-9 rounded-md border border-border bg-surface px-3 text-sm">
          {(cats?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files[0] && upload.mutate(e.target.files[0])} data-testid="doc-file-input" />
        <Btn onClick={() => fileRef.current?.click()} disabled={upload.isPending} data-testid="doc-upload-btn"><Upload className="h-4 w-4" /> Upload</Btn>
      </PageHeader>

      {docs.length === 0 ? (
        <EmptyState icon={FolderLock} title="Vault is empty for this brand" hint="Upload legal, finance, contracts or brand documents. Documents support tags, versions, archive and restore." />
      ) : (
        <Panel className="divide-y divide-border">
          {docs.map((d) => (
            <div key={d.id} className={cn("flex items-center gap-4 p-3", d.archived && "opacity-60")} data-testid={`doc-row-${d.id}`}>
              <FolderLock className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{d.name}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{d.category} · v{d.current_version} · {(d.size / 1024).toFixed(0)} KB</div>
              </div>
              {d.archived && <StatusBadge tone="muted">Archived</StatusBadge>}
              <Btn size="icon" variant="ghost" onClick={() => api.download(`/documents/${d.id}/download`, d.filename)} data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></Btn>
              <Btn size="icon" variant="ghost" onClick={() => archive.mutate(d)}><Archive className="h-4 w-4" /></Btn>
              <Btn size="icon" variant="ghost" onClick={() => del.mutate(d.id)} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Btn>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}
